import { useToast } from "@/hooks/useToast";
import { useFileStorageStore } from "@/store";
import useAuthStore from "@/store/authStore";
import { useThreadStore } from "@/store/index";
import { INITIAL_THREAD_CONTEXT_STATE } from "@/store/threadStore";
import { supabase } from "@/lib/supabase";
import {
  deleteThreadWithQueue,
  isThreadBeingDeleted,
} from "@/lib/utils/threadDeletionQueue";
import { SupabaseThreadUtils } from "@/lib/db/utils/supabase/threads";
import { SupabaseWorkspaceUtils } from "@/lib/db/utils/supabase/workspace";
import { useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

export function useThreadActions({
  threads,
  workspaceThreads,
  setWorkspaceThreads,
  workspaces,
  setWorkspaces,
  markThreadAsNew,
}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const {
    currentThreadId,
    setCurrentThread,
    setCurrentThreadId,
    setCurrentThreadTitle,
    setCurrentThreadContext,
    setThreadStoreLoading,
  } = useThreadStore();
  const { getFileByFullPath } = useFileStorageStore();

  // Thread renaming dialog state
  const [threadRenameDialogOpen, setThreadRenameDialogOpen] = useState(false);
  const [threadToRename, setThreadToRename] = useState(null);
  const [newThreadName, setNewThreadName] = useState("");
  const [isRenamingThread, setIsRenamingThread] = useState(false);

  // Loading and UI state
  const [isLoading, setIsLoading] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  const handleThreadSelect = useCallback(
    async (e, threadId) => {
      if (e && typeof e.stopPropagation === "function") {
        e.stopPropagation();
      }

      localStorage.removeItem("intentionalProjectNavigation");

      setThreadStoreLoading(true);

      try {
        let threadToSelect = threads.find((t) => t.id === threadId);

        if (!threadToSelect) {
          const { data: threadData, error } = await supabase
            .from("threads")
            .select("*")
            .eq("id", threadId)
            .single();

          if (error) {
            throw new Error(`Failed to fetch thread: ${error.message}`);
          }

          if (!threadData) {
            throw new Error("Thread not found in database");
          }

          threadToSelect = threadData;
        }

        // Set initial thread state
        useThreadStore.setState({
          currentThread: threadToSelect,
          currentThreadId: threadToSelect.id,
          currentThreadTitle: threadToSelect.title || "Untitled Chat",
          threadSwitchInProgress: true,
          preventNewMessageClear: true,
          isLoading: true,
        });

        const now = Date.now();
        localStorage.setItem("threadSelectedFromSidebar", threadToSelect.id);
        localStorage.setItem("threadSelectionTimestamp", now.toString());
        localStorage.setItem("lastVisitedThreadId", threadToSelect.id);

        const threadContext = { ...INITIAL_THREAD_CONTEXT_STATE };

        const { data: threadMessages, error: messagesError } = await supabase
          .from("thread_messages")
          .select("*")
          .eq("thread_id", threadId)
          .order("created_at", { ascending: true });

        if (messagesError) {
          console.error("Error fetching thread messages:", messagesError);
          throw messagesError;
        }

        const chronologicallySortedMessages = threadMessages
          ? [...threadMessages].sort((a, b) => {
              const aDate = new Date(a.created_at).getTime();
              const bDate = new Date(b.created_at).getTime();
              return aDate - bDate;
            })
          : [];

        // Simplified message processing
        const text_content = [];
        const code_blocks = [];
        const image_files = [];
        const attachments = [];
        const annotations = [];
        const diagrams = [];
        const image_urls = [];
        const storage_files = [];
        const message_metadata = [];

        let validStorageFiles = [];
        if (storage_files && storage_files.length > 0) {
          const storageFilePathData = storage_files || [];
          const storageFiles = await Promise.all(
            storageFilePathData.map(async (data) => {
              try {
                if (!data?.path) return null;
                return await getFileByFullPath("chat", data.path);
              } catch (err) {
                console.error("Error fetching storage file:", err);
                return null;
              }
            })
          );
          validStorageFiles = storageFiles.filter(Boolean);
        }

        Object.assign(threadContext, {
          text_content: text_content || "",
          code_blocks: code_blocks || [],
          image_files: image_files || [],
          attachments: attachments || [],
          annotations: annotations || [],
          diagrams: diagrams || [],
          image_urls: image_urls || [],
          metadata: threadToSelect.metadata || {},
          messages: chronologicallySortedMessages || [],
          storage_files: storage_files || [],
          message_metadata: message_metadata || {},
          files: validStorageFiles,
          title:
            threadToSelect.title ||
            threadToSelect.metadata?.title ||
            "Untitled Chat",
          message_count: chronologicallySortedMessages?.length || 0,
        });

        await Promise.all([
          setCurrentThread({
            ...threadToSelect,
            updated_at: threadToSelect?.updated_at
              ? new Date(threadToSelect.updated_at)
              : new Date(),
          }),
          setCurrentThreadId(threadToSelect.id),
          setCurrentThreadTitle(threadToSelect.title || "Untitled Chat"),
          setCurrentThreadContext(threadContext),
        ]);

        const messagesWithFiles = chronologicallySortedMessages?.map(
          (message) => {
            try {
              if (!message?.metadata?.storage_files) return message;

              let parsedStorageFiles = [];

              if (typeof message.metadata.storage_files === "string") {
                if (
                  message.metadata.storage_files.startsWith("[") &&
                  message.metadata.storage_files.endsWith("]")
                ) {
                  try {
                    parsedStorageFiles = JSON.parse(
                      message.metadata.storage_files
                    );
                  } catch (jsonError) {
                    parsedStorageFiles = message.metadata.storage_files
                      .split(",")
                      .map((path) => ({ path: path.trim() }));
                  }
                } else {
                  parsedStorageFiles = message.metadata.storage_files
                    .split(",")
                    .map((path) => ({ path: path.trim() }));
                }
              } else if (Array.isArray(message.metadata.storage_files)) {
                parsedStorageFiles = message.metadata.storage_files;
              }

              const files = parsedStorageFiles
                .map((file) => {
                  try {
                    const path = typeof file === "string" ? file : file?.path;
                    return path ? getFileByFullPath("chat", path) : null;
                  } catch (err) {
                    console.error("Error processing message file:", err);
                    return null;
                  }
                })
                .filter(Boolean);

              return { ...message, files };
            } catch (err) {
              console.error("Error processing message:", err);
              return message;
            }
          }
        );

        useThreadStore.setState({
          messages: messagesWithFiles,
          isLoading: false,
        });

        let workspaceId;
        for (const [wsId, wsThreads] of Object.entries(workspaceThreads)) {
          if (wsThreads.some((t) => t.id === threadId)) {
            workspaceId = wsId;
            break;
          }
        }

        if (workspaceId) {
          localStorage.setItem("workspaceThreadContext", workspaceId);
          localStorage.setItem(
            "isSharedThread",
            threadToSelect.metadata?.is_shared_thread === true
              ? "true"
              : "false"
          );
        }

        // Check if we're coming from a project page - if so, use replace to avoid page reload
        const isFromProjectPage = location.pathname.startsWith("/projects/");
        const navigateOptions = isFromProjectPage
          ? { replace: true, state: { preventReload: true } }
          : { state: { preventReload: true } };

        navigate(`/chat/${threadId}`, navigateOptions);

        setTimeout(() => {
          const event = new CustomEvent("force-scroll-to-bottom", {
            detail: { threadId, source: "workspace-navigation" },
          });
          window.dispatchEvent(event);
        }, 300);
      } catch (error) {
        console.error("Error selecting thread:", error);
        setThreadStoreLoading(false);
      }
    },
    [
      threads,
      workspaceThreads,
      navigate,
      getFileByFullPath,
      setCurrentThread,
      setCurrentThreadId,
      setCurrentThreadTitle,
      setCurrentThreadContext,
      setThreadStoreLoading,
    ]
  );

  const handleThreadDelete = useCallback(
    async (e, thread, workspaceId) => {
      if (e && typeof e.stopPropagation === "function") {
        e.stopPropagation();
      }

      if (isThreadBeingDeleted(thread.id)) {
        console.log(`Thread ${thread.id} is already queued for deletion`);
        return;
      }

      const isCurrentThread = currentThreadId === thread.id;

      const vectorStoreId =
        thread.metadata?.vector_store_id ||
        thread.vector_store_id ||
        thread.metadata?.assistant_id?.vector_store_id ||
        thread.metadata?.assistant_id?.metadata?.vector_store_id;

      const success = await deleteThreadWithQueue(thread.id, {
        isCurrentThread,
        workspaceId,
        vectorStoreId,
        originalThread: thread,
        navigate: isCurrentThread ? navigate : null,
        setWorkspaceThreads,
        setWorkspaces,
        showErrorToast: (message) => {
          toast({
            title: "Error",
            description: message,
            variant: "destructive",
          });
        },
        onSuccess: async (threadId) => {
          console.log(`Thread ${threadId} deleted successfully`);

          try {
            const currentWorkspace = workspaces.find(
              (w) => w.id === workspaceId
            );

            if (currentWorkspace?.workspace_threads) {
              const { data: workspaceThreadsData, error } = await supabase
                .from("threads")
                .select(
                  "id, title, openai_thread_id, status, updated_at, metadata, is_team, is_shared, workspace_id"
                )
                .in("id", currentWorkspace.workspace_threads)
                .order("updated_at", { ascending: true });

              if (!error && workspaceThreadsData) {
                const validThreads = workspaceThreadsData
                  .filter(
                    (thread) =>
                      !thread.metadata?.moved_to_history_at &&
                      (thread.is_team === true ||
                        thread.workspace_id === workspaceId)
                  )
                  .map((thread) => ({
                    id: thread.id,
                    title:
                      thread.title || `Chat ${thread.id.substring(0, 6)}...`,
                    status: thread.status || "active",
                    openai_thread_id: thread.openai_thread_id,
                    lastMessageAt: thread.updated_at,
                    metadata: thread.metadata,
                  }));

                setWorkspaceThreads((prev) => ({
                  ...prev,
                  [workspaceId]: validThreads,
                }));
              }
            }
          } catch (refreshError) {
            console.warn(
              "Error during thread refresh after deletion:",
              refreshError
            );
          }
        },
        onError: (threadId, error) => {
          console.error(`Failed to delete thread ${threadId}:`, error);
          toast({
            title: "Error",
            description: "Failed to delete thread. Please try again.",
            variant: "destructive",
          });
        },
      });

      if (!success) {
        toast({
          title: "Error",
          description: "Failed to queue thread for deletion. Please try again.",
          variant: "destructive",
        });
      }
    },
    [
      currentThreadId,
      navigate,
      toast,
      workspaces,
      setWorkspaceThreads,
      setWorkspaces,
    ]
  );

  const startRenaming = useCallback((e, thread) => {
    if (e && typeof e.stopPropagation === "function") {
      e.stopPropagation();
      e.preventDefault();
    }

    setOpenDropdownId(null);
    setThreadToRename(thread);
    setNewThreadName(thread.title || "");
    setThreadRenameDialogOpen(true);
  }, []);

  const handleThreadRenameCancel = useCallback(() => {
    setThreadRenameDialogOpen(false);
    setThreadToRename(null);
    setNewThreadName("");
  }, []);

  const handleThreadRenameSubmit = useCallback(async () => {
    if (!threadToRename || !newThreadName.trim()) return;

    try {
      setIsRenamingThread(true);

      const { data, error } = await supabase
        .from("threads")
        .update({
          title: newThreadName.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", threadToRename.id)
        .select("*")
        .single();

      if (error) {
        console.error("Error updating thread title in Supabase:", error);
        throw error;
      }

      let threadWorkspaceId = null;
      for (const [workspaceId, workspaceThreadsList] of Object.entries(
        workspaceThreads
      )) {
        if (workspaceThreadsList.some((t) => t.id === threadToRename.id)) {
          threadWorkspaceId = workspaceId;
          break;
        }
      }

      if (threadWorkspaceId) {
        setWorkspaceThreads((prev) => {
          const updatedThreads =
            prev[threadWorkspaceId]?.map((t) =>
              t.id === threadToRename.id
                ? { ...t, title: newThreadName.trim() }
                : t
            ) || [];
          return {
            ...prev,
            [threadWorkspaceId]: updatedThreads,
          };
        });
      }

      useThreadStore.setState((state) => {
        const threadIsCurrentThread =
          state.currentThread?.id === threadToRename.id;

        return {
          threads: state.threads.map((t) =>
            t.id === threadToRename.id
              ? {
                  ...t,
                  title: newThreadName.trim(),
                  updated_at: new Date().toISOString(),
                }
              : t
          ),
          currentThread: threadIsCurrentThread
            ? {
                ...state.currentThread,
                title: newThreadName.trim(),
                updated_at: new Date().toISOString(),
              }
            : state.currentThread,
          currentThreadTitle: threadIsCurrentThread
            ? newThreadName.trim()
            : state.currentThreadTitle,
        };
      });

      setThreadRenameDialogOpen(false);
      setThreadToRename(null);
      setNewThreadName("");
    } catch (error) {
      console.error("Error renaming thread:", error);
    } finally {
      setIsRenamingThread(false);
    }
  }, [threadToRename, newThreadName, workspaceThreads, setWorkspaceThreads]);

  const handleToggleBookmark = useCallback(
    async (e, thread) => {
      if (e && typeof e.stopPropagation === "function") {
        e.stopPropagation();
      }
      try {
        setIsLoading(true);

        const isBookmarked = thread.metadata?.is_bookmarked || false;

        const updatedMetadata = {
          ...(thread.metadata || {}),
          is_bookmarked: !isBookmarked,
          bookmark_updated_at: new Date().toISOString(),
        };

        const { thread: updatedThread, error } =
          await SupabaseThreadUtils.updateThreadMetadata({
            supabaseThreadId: thread.id,
            metadata: updatedMetadata,
          });

        if (error) {
          console.error("Error toggling bookmark:", error);
          throw error;
        }

        // Update workspaceThreads first (threads in projects are always in workspaceThreads)
        setWorkspaceThreads((prev) => {
          const updated = { ...prev };
          for (const [workspaceId, wsThreads] of Object.entries(updated)) {
            const threadIndex = wsThreads.findIndex((t) => t.id === thread.id);
            if (threadIndex !== -1) {
              updated[workspaceId] = wsThreads.map((t) =>
                t.id === thread.id ? { ...t, metadata: updatedMetadata } : t
              );
              break;
            }
          }
          return updated;
        });

        // Update main threads array if thread exists there
        const fullThread = threads.find((t) => t.id === thread.id);
        if (fullThread) {
          useThreadStore.setState((state) => {
            const threadIsCurrentThread = state.currentThread?.id === thread.id;

            return {
              threads: state.threads.map((t) =>
                t.id === thread.id ? { ...t, metadata: updatedMetadata } : t
              ),
              currentThread: threadIsCurrentThread
                ? { ...state.currentThread, metadata: updatedMetadata }
                : state.currentThread,
            };
          });
        }
      } catch (error) {
        console.error("Error toggling bookmark:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [threads, setWorkspaceThreads]
  );

  const handleThreadClone = useCallback(
    async (e, thread, workspaceId) => {
      if (e && typeof e.stopPropagation === "function") {
        e.stopPropagation();
      }
      try {
        setIsLoading(true);

        if (!workspaceId) {
          throw new Error(
            "No workspace ID provided. Please try again from a workspace."
          );
        }

        const workspace = workspaces.find((w) => w.id === workspaceId);
        if (!workspace) {
          throw new Error("Workspace not found");
        }

        const { data: originalThread, error: threadError } = await supabase
          .from("threads")
          .select("*")
          .eq("id", thread.id)
          .single();

        if (threadError) {
          throw new Error(`Failed to fetch thread: ${threadError.message}`);
        }

        const { data: originalMessages, error: messagesError } = await supabase
          .from("thread_messages")
          .select("*")
          .eq("thread_id", thread.id)
          .order("created_at", { ascending: true });

        if (messagesError) {
          throw new Error(`Failed to fetch messages: ${messagesError.message}`);
        }

        const newThreadTitle = `Clone of ${thread.title || "Untitled Chat"}`;
        const timestamp = new Date().toISOString();

        const cloneId = `clone-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;

        const threadMetadata = {
          ...originalThread.metadata,
          cloned_from: thread.id,
          cloned_at: timestamp,
          original_title: thread.title,
          clone_id: cloneId,
          user_id: user.id,
          workspace_id: workspaceId,
          is_team: workspace.is_team === true,
          is_shared_thread: workspace.is_team === true,
          shared_in_workspace: workspace.is_team === true ? workspaceId : null,
        };

        const { data: newThread, error: newThreadError } = await supabase
          .from("threads")
          .insert({
            title: newThreadTitle,
            user_id: user.id,
            workspace_id: workspaceId,
            metadata: threadMetadata,
            created_at: timestamp,
            updated_at: timestamp,
            provider: originalThread.provider || "openai",
            status: "active",
            is_team: workspace.is_team === true,
            is_shared: workspace.is_team === true,
          })
          .select("*")
          .single();

        if (newThreadError) {
          throw new Error(
            `Failed to create new thread: ${newThreadError.message}`
          );
        }

        let insertedMessages = [];
        if (originalMessages && originalMessages.length > 0) {
          const messagesToInsert = originalMessages.map((message) => {
            const newMessageId = `msg_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

            return {
              thread_id: newThread.id,
              message_id: newMessageId,
              role: message.role,
              content: message.content,
              metadata: message.metadata || {},
              created_at: timestamp,
              workspace_id: workspaceId,
              user_id: user.id,
              provider: message.provider || "openai",
              file_ids: message.file_ids || [],
              tools_count: message.tools_count || 0,
              attachments: message.attachments,
              annotations: message.annotations,
              tool_calls: message.tool_calls,
            };
          });

          for (let i = 0; i < messagesToInsert.length; i += 100) {
            const batch = messagesToInsert.slice(i, i + 100);
            const { data: insertedBatch, error: messagesInsertError } =
              await supabase.from("thread_messages").insert(batch).select("*");

            if (messagesInsertError) {
              console.error(
                `Error inserting messages batch ${i / 100 + 1}:`,
                messagesInsertError
              );
            } else if (insertedBatch) {
              insertedMessages = [...insertedMessages, ...insertedBatch];
            }
          }

          const { error: threadUpdateError } = await supabase
            .from("threads")
            .update({
              message_count: originalMessages.length,
              last_message_at: timestamp,
              last_activity: timestamp,
            })
            .eq("id", newThread.id);

          if (threadUpdateError) {
            console.error(
              "Error updating thread message count:",
              threadUpdateError
            );
          }
        }

        await SupabaseWorkspaceUtils.addThreadToWorkspace(
          workspaceId,
          String(newThread.id)
        );

        const updatedWorkspace =
          await SupabaseWorkspaceUtils.getWorkspace(workspaceId);

        setWorkspaces(
          workspaces.map((w) => (w.id === workspaceId ? updatedWorkspace : w))
        );

        setWorkspaceThreads((prev) => {
          const updatedThreads = [...(prev[workspaceId] || [])];
          const clonedThread = {
            id: newThread.id,
            title: newThreadTitle,
            status: "active",
          };
          updatedThreads.push(clonedThread);

          if (markThreadAsNew) {
            markThreadAsNew(newThread.id, newThreadTitle);
          }

          return {
            ...prev,
            [workspaceId]: updatedThreads,
          };
        });

        const chronologicallySortedMessages = insertedMessages.sort((a, b) => {
          const aDate = new Date(a.created_at).getTime();
          const bDate = new Date(b.created_at).getTime();
          return aDate - bDate;
        });

        useThreadStore.setState((state) => ({
          threads: [newThread, ...state.threads],
          messages: chronologicallySortedMessages,
        }));

        toast({
          title: "Success",
          description: "Thread cloned successfully",
        });

        navigate(`/chat/${newThread.id}`);
      } catch (error) {
        console.error("Error cloning thread:", error);
        toast({
          title: "Error",
          description:
            error.message || "Failed to clone thread. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      workspaces,
      user,
      setWorkspaces,
      setWorkspaceThreads,
      markThreadAsNew,
      navigate,
    ]
  );

  const handleMoveToProject = useCallback(
    // currentWorkspaceId is kept for the caller API but no longer needed: the
    // atomic RPC removes the thread from whatever workspace(s) it's in.
    async (thread, targetWorkspaceId, _currentWorkspaceId) => {
      try {
        setIsLoading(true);

        // Atomically move the chat: removes it from all workspaces, adds it to
        // the target, updates threads.workspace_id + display_order, and updates
        // metadata — all in a single transaction (SECURITY DEFINER RPC). This
        // replaces the previous multi-step manual sequence (remove-from-source →
        // add-to-target → set workspace_id → update metadata), which could orphan
        // a chat when a middle step failed: the chat would be removed from its
        // source and have workspace_id set, but never get added to the target's
        // workspace_threads array (so it vanished from every project and the move
        // reported failure). The RPC is the same one drag-and-drop already uses
        // (see useChatMove.js) and it rolls back on any error.
        const userId = user?.id || thread.user_id;
        const { data: result, error: rpcError } = await supabase.rpc(
          "move_thread_to_workspace_atomic",
          {
            p_thread_id: thread.id,
            p_target_workspace_id: targetWorkspaceId,
            p_display_order: 0, // put at top
            p_user_id: userId,
          }
        );

        if (rpcError) throw rpcError;
        if (result?.success !== true) {
          throw new Error("Move operation did not complete successfully");
        }

        // Update local workspace state to mirror the atomic move: drop the thread
        // from every workspace's list, then add it to the target's.
        const updatedWorkspaces = workspaces.map((ws) => {
          const without = (ws.workspace_threads || []).filter(
            (id) => id !== thread.id
          );
          return ws.id === targetWorkspaceId
            ? { ...ws, workspace_threads: [...without, thread.id] }
            : { ...ws, workspace_threads: without };
        });
        setWorkspaces(updatedWorkspaces);

        // Update thread store
        useThreadStore.setState((state) => {
          const updatedThreads = state.threads.map((t) =>
            t.id === thread.id
              ? {
                  ...t,
                  workspace_id: targetWorkspaceId,
                  metadata: {
                    ...t.metadata,
                    workspace_id: targetWorkspaceId,
                    moved_to_history_at: targetWorkspaceId
                      ? null
                      : new Date().toISOString(),
                  },
                }
              : t
          );
          return { threads: updatedThreads };
        });

        // Show success toast
        const targetName = targetWorkspaceId
          ? workspaces.find((ws) => ws.id === targetWorkspaceId)?.name ||
            "project"
          : "Chats";
        toast({
          title: "Chat moved successfully",
          description: `Chat moved to ${targetName}`,
        });
      } catch (error) {
        console.error("Error moving thread to project:", error);
        toast({
          title: "Error moving chat",
          description: "Failed to move chat to project. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [workspaces, setWorkspaces, toast, user]
  );

  return {
    // State
    isLoading,
    openDropdownId,
    threadRenameDialogOpen,
    threadToRename,
    newThreadName,
    isRenamingThread,

    // Setters
    setOpenDropdownId,
    setThreadRenameDialogOpen,
    setNewThreadName,

    // Handlers
    handleThreadSelect,
    handleThreadDelete,
    handleThreadRenameSubmit,
    startRenaming,
    handleThreadRenameCancel,
    handleToggleBookmark,
    handleThreadClone,
    handleMoveToProject,
  };
}
